import { type JanusCtx, type Row, ecId, handBack, itemField, record, upper } from '../context.js';

const DAMAGE_ROLL = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
const DOT_ROLL = '77A044B7-7041-CA96-3C63-BB718B15A364';
import { type JanusInput } from '../janus.js';
import { noRoll } from './no-roll.js';
import { contestingRoll } from './contesting-roll.js';
import { effectTargetProceeds, effectTargets } from '../is-target-valid-for-effect.js';
import { healingRoll } from './healing-roll.js';
import { regenerationRoll } from './regeneration-roll.js';
import { attackRoll } from './attack-roll.js';
import { damageTypeRoll, withFragmentGateStatuses } from './damage-type-roll.js';
import { postEffectConditions } from '../post-effect-conditions.js';
import { increaseLuckRoll, increaseManaRoll, reduceManaRoll } from './increase-mana-roll.js';
import { tempVRoll } from './temp-v-roll.js';
import { itemTypeAgainstPreDraw, fireItemTypeAgainst } from '../item-type-against-trigger.js';
import { CONTESTING_ROLL, chainSpec } from './chain-leap.js';
import { summonRoll } from './summon-roll.js';
import { SUMMON_ROLL } from '../summon.js';
import { fabricateOnCast } from '../fabrication.js';

// Named legacy handlers whose bodies this dispatch does not yet serve. Other catalog roll types use legacy's default no_roll.
const UNPORTED_ROLL_TYPES = new Set([
  'ED5B44EA-7438-9582-A388-04EFF342E460', // DC
  'F5BC82EE-DBDF-4594-F850-0C49E4292AC7', // temporary maximum vitality
  'DE5B6A74-0024-FFDB-C5C6-B0248D956515', // reduce luck
  '2FE8DFB7-FE61-4862-AF71-3523262350A0', // weapon melee attack
  '3F85CA97-F9B2-A83A-FDE8-E523FD99346C', // weapon ranged attack
  SUMMON_ROLL, // summon requires the caller's spawn contract
]);

/** includes/functions/janus/core/janus_dispatch_roll_types.php:janus_dispatch_roll_types */
export function janusDispatchRollTypes(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  fabricateOnCast(ctx, input, ability, caster);
  withRollDispatch(ctx, input, ability, caster, targets, () => dispatchRollTypes(ctx, input, ability, caster, targets));
}

export function withRollDispatch(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[], dispatch: () => void): void {
  // Wizard v9 Grim Harvest (summon.ts fireCorpseUse): the cast went off — whatever its targets' saves
  if (!ctx.trigger && targets.length) ctx.castsWentOff = (ctx.castsWentOff ?? 0) + 1;
  if (!['damage_only', 'end_turn_in_p_aoe', 'moving_into_p_aoe'].includes(String(input.useAbilityVariable1))) {
    itemTypeAgainstPreDraw(ctx, ability, caster, targets);
    fireItemTypeAgainst(ctx, ability, caster, targets);
  }
  const prior = ctx.dispatchTargetIds;
  ctx.dispatchTargetIds = new Set(targets.map(ecId).filter(Boolean));
  try { dispatch(); } finally { ctx.dispatchTargetIds = prior; }
}
function dispatchRollTypes(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  const rollType = ctx.read.value(ability, 'roll_type');
  // CHAIN (chain_targets_max, R-175): only a Contesting Roll host leaps (contesting-roll.ts); any other host with leap picks → PHP.
  if (targets.length > 1 && chainSpec(ctx.read, ability) && String(rollType ?? '').toUpperCase() !== CONTESTING_ROLL) handBack('chain_leap:roll-type');
  if (String(rollType ?? '').toUpperCase() === 'AD80D31D-898B-66CE-0894-1335D11B1529'
      || (String(rollType ?? '').toUpperCase() === '77A044B7-7041-CA96-3C63-BB718B15A364'
        && ctx.read.has(ability, 'skill_roll_contestee'))) {
    contestingRoll(ctx, input, ability, caster, targets);
    return;
  }
  if (String(rollType ?? '').toUpperCase() === '4FB7452A-F130-40C3-83E6-5E92C329D7B2') {
    healingRoll(ctx, input, ability, caster, targets);
    return;
  }
  if (['9909BE44-99AE-8780-BB12-BD99BA6E672E', '4634635E-4E82-DFB3-8411-27A6BFB8CF0F'].includes(upper(rollType))) {
    regenerationRoll(ctx, input, ability, caster, targets);
    return;
  }
  if (String(rollType ?? '').toUpperCase() === 'DB30C685-3830-9AEA-D8FA-D92FEA762982') {
    increaseManaRoll(ctx, input, ability, caster, targets);
    return;
  }
  if (upper(rollType) === '905613F5-A045-BC0A-3686-CD1EC15060C5') {
    reduceManaRoll(ctx, input, ability, caster, targets);
    return;
  }
  // R-130 ask 3 (lane A7, 2026-10-02): increase_luck_roll.php — increase_mana_roll.php's twin on luck / fortune (King Dice / Lucky Refreshment)
  if (String(rollType ?? '').toUpperCase() === 'C823C739-A9DE-AA5A-07B8-CB67F578F880') {
    increaseLuckRoll(ctx, input, ability, caster, targets);
    return;
  }
  if (String(rollType ?? '').toUpperCase() === '5E2689A2-0F7D-1E27-F4D5-CA09CA5C2591') {
    tempVRoll(ctx, input, ability, caster, targets);
    return;
  }
  // Wizard v9 WP3 (2026-10-09): summon_roll.php — the plain Summon Roll (summon-roll.ts); its summons are decided in post_effect_conditions
  if (String(rollType ?? '').toUpperCase() === SUMMON_ROLL && ctx.summons != null) {
    summonRoll(ctx, input, ability, caster, targets);
    return;
  }
  if (['5740CA49-006E-A7A0-B46B-6DDED6CD546C', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C'].includes(String(rollType ?? '').toUpperCase())) {
    attackRoll(ctx, input, ability, caster, targets);
    return;
  }
  // A16 (EC1 CE1 / CE2, 2026-10-09): janus_dispatch_roll_types.php routes a Damage Over Time Roll WITHOUT skill_roll_contestee to damage_roll()
  // exactly like a Damage Roll (the contested one went to contestingRoll above) — the zone / orb pulse that handed every such End Turn back.
  // damage_roll.php's session roll type (~L89) is the DoT type for it: the caster's makes-roll-type carriers are judged on that type.
  const sessionRollType = String(rollType ?? '').toUpperCase() === DOT_ROLL ? DOT_ROLL : DAMAGE_ROLL;
  if (String(rollType ?? '').toUpperCase() === '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0' || String(rollType ?? '').toUpperCase() === DOT_ROLL) {
    // damage_roll.php's FOREACH TARGET: prepare each target's damage dice, run its effects, then apply its damage if the gate passes.
    // The post-loop block (caster-side contest statuses, contingent / random-table payloads)
    // runs only for a contest, which this branch never is (77A044B7 + contestee routes to contestingRoll above). Proved against a
    // synthetic PHP capture: Oni Flame (a placed circle) over the PC and a creature (tools/test/artifacts/janus-ability-oracle-2026-09-29-a3).
    if (!targets.length) handBack('damage_roll:no-target');
    // damage_roll.php ~L107 fires the caster's triggers_when_caster_makes_roll_type [Damage Roll] ONCE before the loop — not executed
    // here, so a carrier keyed to it → PHP (Codex review 2026-09-29).
    for (const bucket of ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest', 'feat']) {
      for (const entry of ctx.read.entries(caster, bucket)) {
        const row = record(entry);
        const id = upper(typeof entry === 'string' ? entry : row?.ability_id ?? itemField(record(row?.item_arr), 'item_id') ?? itemField(row, 'item_id'));
        const def = record(row?.item_arr) ?? (id ? ctx.read.definition(id) : null);
        if (!def) handBack('damage_roll:caster-roll-trigger-carrier-not-held');
        if (ctx.read.entries(def!, 'triggers_when_caster_makes_roll_type').some((e) => upper(itemField(record(e), 'item_id') ?? e) === sessionRollType))
          handBack('damage_roll:caster-makes-damage-roll-trigger');
      }
    }
    for (const target of targets) {
      if (!effectTargetProceeds(ctx, input, ability, caster, target, target === targets[0])) continue;
      damageRollOneTarget(ctx, input, ability, caster, target);
    }
    return;
  }
  if (UNPORTED_ROLL_TYPES.has(upper(rollType))) {
    // Unported roll bodies (DC, temp-max vitality, resource reductions) still owe no roll for an excluded recipient.
    // A recipient that proceeds retains the existing named refusal until that handler is ported.
    if (targets.length && !effectTargets(ctx, input, ability, caster, targets)) return;
    handBack(`janus_dispatch_roll_types:${String(rollType)}`);
  }
  const type = String(itemField(ability, 'item_type') ?? '');
  if (rollType == null && (type === 'attack_ability' || type === 'opportunity_attack_ability')) handBack('janus_dispatch_roll_types:attack-ability');
  noRoll(ctx, input, ability, caster, targets);
}

/** One target of damage_roll.php's loop. */
function damageRollOneTarget(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row): void {
  // Prepare every recipient's fragment before its effects, including a recipient whose gate will fail. The shared prepared path
  // skips only that application, keeps later recipients' dice aligned, and isolates fragment modifiers from effect-time changes.
  damageTypeRoll(ctx, input, ability, caster, target, false, false, 0, false,
    () => postEffectConditions(ctx, input, ability, caster, target));
}
